import { useEffect, useMemo, useRef, useState } from "react";
import { getRole, ROLES, type RoleId } from "@/data/roles.ts";
import { markerUrl, type MarkerIcon } from "@/data/markers.ts";
import { orderText, squadColor, squadTag, UNASSIGNED_SQUAD } from "@/data/squads.ts";
import { currentOrderFor } from "@/session/sessionDoc.ts";
import type { Player } from "@/session/schemas.ts";
import type { SessionApi } from "@/session/useSession.ts";
import { useNow } from "@/session/useNow.ts";

interface SquadRosterProps {
  readonly session: SessionApi;
  readonly myId: string;
  readonly myRole: RoleId;
  readonly myCallsign: string;
  /** Change the local player's class. Takes effect for everyone at once. */
  readonly onChangeRole: (role: RoleId) => void;
  readonly onChangeCallsign: (callsign: string) => void;
}

/**
 * The order of battle.
 *
 * Grouped by squad, because that is the unit a commander thinks in. Each squad
 * shows its current order and how long ago it was given — a squad with no
 * order at all is the thing a commander most needs to spot, so an idle squad
 * simply has no order card.
 *
 * Squads are never pre-seeded. This panel is where one comes into being: a
 * player types a name and creates it, or joins one a teammate already made.
 * A squad lives exactly as long as somebody is in it — the last member out
 * deletes it (see `assignPlayerSquad`), so the list only ever shows real
 * squads and never a stale slot the commander could task by mistake.
 */
export function SquadRoster({
  session,
  myId,
  myRole,
  myCallsign,
  onChangeRole,
  onChangeCallsign,
}: SquadRosterProps): React.ReactElement {
  const now = useNow();
  const [historyOpenFor, setHistoryOpenFor] = useState<ReadonlySet<string>>(new Set());
  const toggleHistory = (squadId: string): void => {
    setHistoryOpenFor((prev) => {
      const next = new Set(prev);
      if (next.has(squadId)) next.delete(squadId);
      else next.add(squadId);
      return next;
    });
  };

  const presentIds = useMemo(
    () => new Set(session.present.map((entry) => entry.id)),
    [session.present],
  );

  const bySquad = useMemo(() => {
    const groups = new Map<string, Player[]>();
    for (const player of session.players) {
      const key = player.squad || UNASSIGNED_SQUAD;
      const list = groups.get(key) ?? [];
      list.push(player);
      groups.set(key, list);
    }
    for (const list of groups.values()) {
      list.sort((a, b) => {
        const aHere = presentIds.has(a.id) ? 0 : 1;
        const bHere = presentIds.has(b.id) ? 0 : 1;
        return aHere - bHere || a.callsign.localeCompare(b.callsign);
      });
    }
    return groups;
  }, [session.players, presentIds]);

  const unassigned = bySquad.get(UNASSIGNED_SQUAD) ?? [];

  // Oldest first: the squad someone made when the session started stays at
  // the top rather than jumping around as newer ones are created.
  const squads = useMemo(
    () => [...session.squads].sort((a, b) => a.createdAt - b.createdAt),
    [session.squads],
  );

  // An empty roster and one that has not finished pulling in a teammate's
  // squads look the same for a moment. Say so, rather than inviting someone
  // to create a squad that is about to sync in and duplicate it.
  const settling = session.sync.status !== "connected" && session.sync.status !== "error";

  return (
    <aside className="flex w-72 shrink-0 flex-col border-l border-border-subtle bg-surface">
      <header className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
        <span className="text-xs font-medium tracking-wide text-text-secondary uppercase">
          Order of battle
        </span>
        <span className="numeric text-xs text-text-muted">{session.present.length} online</span>
      </header>

      {!session.mySquad && (
        <div className="border-b border-warning/40 bg-warning/10 px-3 py-1.5">
          <p className="text-[11px] font-medium text-warning">
            Join a squad or create one to receive orders.
          </p>
        </div>
      )}

      <CreateSquadForm onCreate={session.createSquad} disabled={settling} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {squads.length === 0 && (
          <p className="px-3 py-3 text-[11px] text-text-muted">
            {settling ? "Connecting…" : "No squads yet. Create the first one above."}
          </p>
        )}

        {squads.map((squad) => {
          const members = bySquad.get(squad.id) ?? [];
          const order = currentOrderFor(session.orders, squad.id);
          const liveCount = members.filter((member) => presentIds.has(member.id)).length;
          const isMine = session.mySquad === squad.id;
          const name = squad.name || squad.id;
          const color = squadColor(squad.id);
          const history = session.allOrders.filter(
            (entry) =>
              entry.squadId === squad.id &&
              (entry.status === "cancelled" || entry.status === "complete"),
          );
          const historyOpen = historyOpenFor.has(squad.id);

          return (
            <section
              key={squad.id}
              className="border-b border-border-subtle"
              style={
                isMine
                  ? {
                      backgroundColor:
                        "color-mix(in oklab, var(--color-surface-raised) 60%, transparent)",
                    }
                  : undefined
              }
            >
              <div className="flex items-center gap-2 px-3 py-1.5">
                <span
                  className="numeric flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold"
                  style={{ backgroundColor: color, color: "oklch(0.16 0.01 250)" }}
                >
                  {session.squadTags.get(squad.id) ?? squadTag(name)}
                </span>
                <span className="truncate text-xs font-semibold text-text-primary">{name}</span>
                <span className="numeric text-[11px] text-text-muted">
                  {liveCount}/{members.length}
                </span>

                {!isMine && (
                  <button
                    type="button"
                    onClick={() => session.joinSquad(squad.id)}
                    className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] text-text-muted transition-colors hover:text-accent"
                  >
                    Join
                  </button>
                )}
                {isMine && (
                  <button
                    type="button"
                    onClick={() => session.joinSquad(UNASSIGNED_SQUAD)}
                    className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] text-text-muted transition-colors hover:text-danger"
                  >
                    Leave
                  </button>
                )}
              </div>

              {members.length > 0 && <RoleSummary members={members} />}

              {order &&
                (session.isCommander ? (
                  // Same rule as markers and routes: one tap placed it, one
                  // tap on the thing itself clears it. No separate control.
                  <button
                    type="button"
                    onClick={() => session.cancel(order.id)}
                    title="Click to withdraw this order"
                    className="mx-3 mb-1.5 block w-[calc(100%-1.5rem)] rounded border-l-2 bg-surface-sunken px-2 py-1 text-left transition-colors hover:bg-danger/10"
                    style={{ borderColor: color }}
                  >
                    <div className="numeric text-[11px] font-semibold text-text-primary">
                      {orderText(order.kind, order.place || undefined, order.note)}
                    </div>
                    <div className="text-[10px] text-text-muted">
                      {relativeTime(now, order.issuedAt)}
                    </div>
                  </button>
                ) : (
                  <div
                    className="mx-3 mb-1.5 rounded border-l-2 bg-surface-sunken px-2 py-1"
                    style={{ borderColor: color }}
                  >
                    <div className="numeric text-[11px] font-semibold text-text-primary">
                      {orderText(order.kind, order.place || undefined, order.note)}
                    </div>
                    <div className="text-[10px] text-text-muted">
                      {relativeTime(now, order.issuedAt)}
                    </div>
                  </div>
                ))}

              {members.length === 0 ? (
                <p className="px-3 pb-1.5 text-[11px] text-text-muted">Empty</p>
              ) : (
                <ul className="pb-1">
                  {members.map((member) => (
                    <MemberRow
                      key={member.id}
                      player={member}
                      here={presentIds.has(member.id)}
                      isMe={member.id === myId}
                      isLead={squad.leadId === member.id}
                      isCommander={member.id === session.commanderId}
                      canManage={session.isCommander && member.id !== myId}
                      onMakeLead={
                        squad.leadId === member.id
                          ? undefined
                          : () => session.setSquadLead(squad.id, member.id)
                      }
                      onRemove={() => session.removePlayer(member.id)}
                      myRole={myRole}
                      myCallsign={myCallsign}
                      onChangeRole={onChangeRole}
                      onChangeCallsign={onChangeCallsign}
                      onSetVehicle={session.setVehicle}
                    />
                  ))}
                </ul>
              )}

              {history.length > 0 && (
                <div className="px-3 pb-1.5">
                  <button
                    type="button"
                    onClick={() => toggleHistory(squad.id)}
                    aria-expanded={historyOpen}
                    className="text-[10px] text-text-muted transition-colors hover:text-text-secondary"
                  >
                    {historyOpen ? "Hide history" : `History (${history.length})`}
                  </button>

                  {historyOpen && (
                    <ul className="mt-1 space-y-1 border-l border-border-subtle pl-2">
                      {history.map((entry) => (
                        <li key={entry.id} className="text-[10px] text-text-muted">
                          <span
                            className={
                              entry.status === "complete" ? "text-success" : "text-text-muted"
                            }
                            title={entry.status}
                          >
                            {entry.status === "complete" ? "done" : "cancelled"}
                          </span>{" "}
                          <span className="numeric">
                            {orderText(entry.kind, entry.place || undefined, entry.note)}
                          </span>{" "}
                          · {relativeTime(now, entry.issuedAt)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </section>
          );
        })}

        {unassigned.length > 0 && (
          <section className="border-b border-border-subtle">
            <div className="px-3 py-1.5">
              <span className="text-xs font-semibold text-text-muted">Unassigned</span>
            </div>
            <ul className="pb-1">
              {unassigned.map((member) => (
                <MemberRow
                  key={member.id}
                  player={member}
                  here={presentIds.has(member.id)}
                  isMe={member.id === myId}
                  isLead={false}
                  isCommander={member.id === session.commanderId}
                  canManage={session.isCommander && member.id !== myId}
                  onMakeLead={undefined}
                  onRemove={() => session.removePlayer(member.id)}
                  myRole={myRole}
                  myCallsign={myCallsign}
                  onChangeRole={onChangeRole}
                  onChangeCallsign={onChangeCallsign}
                  onSetVehicle={session.setVehicle}
                />
              ))}
            </ul>
          </section>
        )}
      </div>
    </aside>
  );
}

/**
 * Where a squad is born.
 *
 * No dropdown of pre-set names — a squad does not exist until a player types
 * what their team actually calls it and creates it, which is the whole point
 * of dropping the fixed Alpha–Foxtrot roster.
 */
function CreateSquadForm({
  onCreate,
  disabled,
}: {
  readonly onCreate: (name: string) => string;
  /** True while still connecting — an existing squad may be about to arrive. */
  readonly disabled: boolean;
}): React.ReactElement {
  const [name, setName] = useState("");

  const submit = (): void => {
    const trimmed = name.trim();
    if (!trimmed || disabled) return;
    onCreate(trimmed);
    setName("");
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className="flex items-center gap-1.5 border-b border-border-subtle px-3 py-2"
    >
      <input
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={24}
        disabled={disabled}
        placeholder={disabled ? "Connecting…" : "New squad name"}
        aria-label="New squad name"
        className="w-full min-w-0 rounded border border-border-subtle bg-surface-sunken px-2 py-1 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none disabled:opacity-50"
      />
      <button
        type="submit"
        disabled={disabled || !name.trim()}
        className="shrink-0 rounded border border-border-strong px-2 py-1 text-[11px] text-text-secondary transition-colors hover:text-text-primary disabled:opacity-40"
      >
        Create
      </button>
    </form>
  );
}

function MemberRow({
  player,
  here,
  isMe,
  isLead,
  isCommander,
  canManage,
  onMakeLead,
  onRemove,
  myRole,
  myCallsign,
  onChangeRole,
  onChangeCallsign,
  onSetVehicle,
}: {
  readonly player: Player;
  readonly here: boolean;
  readonly isMe: boolean;
  readonly isLead: boolean;
  readonly isCommander: boolean;
  /** Commander housekeeping is available, and this is not the commander's own row. */
  readonly canManage: boolean;
  /** Undefined when this member already leads the squad, or is unassigned. */
  readonly onMakeLead: (() => void) | undefined;
  readonly onRemove: () => void;
  readonly myRole: RoleId;
  readonly myCallsign: string;
  readonly onChangeRole: (role: RoleId) => void;
  readonly onChangeCallsign: (callsign: string) => void;
  readonly onSetVehicle: (vehicle: string) => void;
}): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(myCallsign);

  const roleIconClass = canManage ? "shrink-0 opacity-80" : "ml-auto shrink-0 opacity-80";

  const commitName = (): void => {
    const trimmed = nameDraft.trim().slice(0, 20);
    if (trimmed && trimmed !== myCallsign) onChangeCallsign(trimmed);
    setEditingName(false);
  };

  return (
    <li className="flex items-center gap-2 px-3 py-0.5" style={{ opacity: here ? 1 : 0.4 }}>
      <span
        className="h-1 w-1 shrink-0 rounded-full"
        style={{
          backgroundColor: here ? "var(--color-success)" : "var(--color-text-muted)",
        }}
        title={here ? "Online" : "Offline"}
      />
      {isMe && editingName ? (
        <input
          value={nameDraft}
          onChange={(event) => setNameDraft(event.target.value)}
          onBlur={commitName}
          onKeyDown={(event) => {
            if (event.key === "Enter") commitName();
            if (event.key === "Escape") setEditingName(false);
          }}
          maxLength={20}
          autoFocus
          className="w-24 min-w-0 rounded border border-accent bg-surface-sunken px-1 text-[11px] text-text-primary focus:outline-none"
        />
      ) : isMe ? (
        <button
          type="button"
          onClick={() => {
            setNameDraft(myCallsign);
            setEditingName(true);
          }}
          title="Click to change your name"
          className="truncate text-[11px] text-text-secondary transition-colors hover:text-accent"
        >
          {player.callsign}
        </button>
      ) : (
        <span className="truncate text-[11px] text-text-secondary">{player.callsign}</span>
      )}
      {isMe && <span className="text-[10px] text-text-muted">you</span>}
      {isLead && (
        <span className="text-[9px] font-semibold text-text-muted" title="Squad lead">
          SL
        </span>
      )}
      {isCommander && (
        <span className="text-[9px] font-semibold text-accent" title="Commander">
          CMD
        </span>
      )}
      {/* Your own class is yours to change — tap the role to pick another. For
          everyone else it is just the game's own icon, read-only, so the
          roster reads like the in-game map does. */}
      {isMe ? (
        <RolePicker className={roleIconClass} current={myRole} onChange={onChangeRole} />
      ) : (
        <>
          <img
            src={markerUrl(getRole(player.role).icon)}
            alt=""
            width={14}
            height={14}
            className={roleIconClass}
            title={getRole(player.role).label}
          />
          <span className="numeric shrink-0 text-[10px] text-text-muted">
            {getRole(player.role).short}
          </span>
        </>
      )}

      {/* What you are in — a tank, a heli, or nothing. Everyone sees it;
          only you can change your own. */}
      {isMe ? (
        <VehiclePicker current={player.vehicle} onChange={onSetVehicle} />
      ) : player.vehicle ? (
        <img
          src={markerUrl(player.vehicle as MarkerIcon)}
          alt={player.vehicle}
          width={14}
          height={14}
          className="shrink-0 opacity-70"
          title={player.vehicle}
        />
      ) : null}

      {/* Commander housekeeping: recover a squad stuck behind an absent lead,
          or drop someone who is never coming back. Collapsed behind one
          small toggle rather than sitting spelled out on every single row —
          this is for the rare moment something needs fixing, not something
          to read every time you glance at who is online. */}
      {canManage && (
        <span className="ml-auto shrink-0">
          {!menuOpen ? (
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              title="Manage this player"
              aria-label={`Manage ${player.callsign}`}
              className="rounded px-1.5 text-text-muted transition-colors hover:text-text-primary"
            >
              ⋯
            </button>
          ) : (
            <span className="flex items-center gap-1">
              {onMakeLead && (
                <button
                  type="button"
                  onClick={() => {
                    onMakeLead();
                    setMenuOpen(false);
                  }}
                  title="Make squad lead"
                  className="rounded px-1 text-[9px] text-text-muted transition-colors hover:text-accent"
                >
                  Lead
                </button>
              )}
              {confirmingRemove ? (
                <button
                  type="button"
                  onClick={() => {
                    setConfirmingRemove(false);
                    onRemove();
                  }}
                  onBlur={() => setConfirmingRemove(false)}
                  autoFocus
                  title="Click again to confirm removal from the roster"
                  className="rounded bg-danger px-1 text-[9px] font-semibold text-surface"
                >
                  Sure?
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(true)}
                  title="Remove from the roster"
                  className="rounded px-1 text-[9px] text-text-muted transition-colors hover:text-danger"
                >
                  Remove
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmingRemove(false);
                }}
                title="Close"
                className="rounded px-1 text-[9px] text-text-muted hover:text-text-primary"
              >
                ×
              </button>
            </span>
          )}
        </span>
      )}
    </li>
  );
}

/**
 * Your own class, and a way to change it.
 *
 * You buy what you want in WARDOGS, so what you are doing this life changes
 * mid-match — picked up a wrench, jumped in a heli. The roster should follow
 * that without leaving the session, so the icon on your own row is the
 * switch. Everyone else's follows the moment you pick, because a commander
 * tasking a "medic" who is actually flying is worse than no label at all.
 */
/**
 * What you are in, not which exact variant — a commander needs to know
 * "someone is in a tank", not "someone is in an L2A6 with the winter camo".
 * One tap, done.
 */
const VEHICLE_OPTIONS: readonly {
  readonly id: string;
  readonly label: string;
  readonly icon: MarkerIcon | "";
}[] = [
  { id: "", label: "On foot", icon: "" },
  { id: "quad", label: "Light vehicle", icon: "quad" },
  { id: "kodiak", label: "Transport", icon: "kodiak" },
  { id: "ural", label: "Truck", icon: "ural" },
  { id: "tank", label: "Tank", icon: "tank" },
  { id: "heli", label: "Helicopter", icon: "heli" },
  { id: "havoc", label: "Gunship", icon: "havoc" },
  { id: "artillery", label: "Artillery", icon: "artillery" },
];

function VehiclePicker({
  current,
  onChange,
}: {
  readonly current: string;
  readonly onChange: (vehicle: string) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const active = VEHICLE_OPTIONS.find((v) => v.id === current);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={
          current
            ? `In: ${active?.label ?? current} — click to change`
            : "On foot — click to set vehicle"
        }
        className="flex items-center gap-0.5 rounded px-0.5 transition-colors hover:bg-surface-raised"
      >
        {current ? (
          <img
            src={markerUrl(current as MarkerIcon)}
            alt=""
            width={13}
            height={13}
            className="opacity-80"
          />
        ) : (
          <span className="text-[9px] text-text-muted">+</span>
        )}
      </button>

      {open && (
        <div className="absolute top-full right-0 z-20 mt-1 w-36 rounded border border-border-strong bg-surface-raised p-1 shadow-lg">
          {VEHICLE_OPTIONS.map((option) => (
            <button
              key={option.id || "__foot"}
              type="button"
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-colors hover:bg-surface-sunken"
              style={
                option.id === current
                  ? { backgroundColor: "var(--color-surface-sunken)" }
                  : undefined
              }
            >
              {option.id ? (
                <img
                  src={markerUrl(option.id as MarkerIcon)}
                  alt=""
                  width={14}
                  height={14}
                  className="opacity-80"
                />
              ) : (
                <span className="inline-block h-3.5 w-3.5 text-center text-[10px] text-text-muted">
                  —
                </span>
              )}
              <span className="text-[11px] text-text-primary">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RolePicker({
  className,
  current,
  onChange,
}: {
  readonly className: string;
  readonly current: RoleId;
  readonly onChange: (role: RoleId) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const role = getRole(current);

  return (
    <div ref={ref} className={`relative flex items-center gap-1 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        title={`Your class: ${role.label} — click to change`}
        className="flex items-center gap-1 rounded px-0.5 transition-colors hover:bg-surface-raised"
      >
        <img src={markerUrl(role.icon)} alt="" width={14} height={14} className="opacity-80" />
        <span className="numeric text-[10px] text-text-secondary">{role.short}</span>
      </button>

      {open && (
        <div className="absolute top-full right-0 z-20 mt-1 w-40 rounded border border-border-strong bg-surface-raised p-1 shadow-lg">
          {ROLES.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => {
                onChange(entry.id);
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-colors hover:bg-surface-sunken"
              style={
                entry.id === current
                  ? { backgroundColor: "var(--color-surface-sunken)" }
                  : undefined
              }
            >
              <img
                src={markerUrl(entry.icon)}
                alt=""
                width={14}
                height={14}
                className="opacity-80"
              />
              <span className="text-[11px] text-text-primary">{entry.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * What the squad is made of, at a glance.
 *
 * A commander deciding who to send somewhere needs to know whether a squad
 * has a medic before they need one, not after — one row of role icons with a
 * count says that without reading every member's row.
 */
function RoleSummary({ members }: { readonly members: readonly Player[] }): React.ReactElement {
  const counts = useMemo(() => {
    const tally = new Map<RoleId, number>();
    for (const member of members) {
      tally.set(member.role, (tally.get(member.role) ?? 0) + 1);
    }
    return ROLES.map((role) => ({ role, count: tally.get(role.id) ?? 0 })).filter(
      (entry) => entry.count > 0,
    );
  }, [members]);

  return (
    <div className="mb-1.5 flex items-center gap-2 px-3">
      {counts.map(({ role, count }) => (
        <span
          key={role.id}
          className="flex items-center gap-0.5"
          title={`${count} × ${role.label}`}
        >
          <img
            src={markerUrl(role.icon)}
            alt=""
            width={12}
            height={12}
            className="opacity-70"
          />
          <span className="numeric text-[10px] text-text-muted">{count}</span>
        </span>
      ))}
    </div>
  );
}

/** "3m ago", coarse on purpose — a history entry needs ordering, not a clock. */
function relativeTime(now: number, at: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}
